/**
 * ProtectedRoute.jsx — Week 4
 * Supports:
 *   requireAdmin  — must be admin or superadmin
 *   requireRole   — must have at least this role (e.g. ROLES.MANAGER)
 * No console.logs.
 */
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useRoles } from "../../hooks/useRoles";
import { useLanguage } from "../../contexts/LanguageContext";
import { motion } from "framer-motion";
import { FiRefreshCw, FiAlertCircle } from "react-icons/fi";

const ProtectedRoute = ({ children, requireAdmin = false, requireRole = null }) => {
  const { user, userData, tenant, loading } = useAuth();
  const { canAccessAdmin, hasRole } = useRoles();
  const { t } = useLanguage();
  const location = useLocation();

  // Show loading UI while auth context is loading, OR while we have a
  // logged-in firebaseUser but their Firestore userData hasn't been
  // fetched yet. This second condition prevents protected pages from
  // briefly rendering with stale/empty data right after sign-in.
  const isWaitingForUserData = user && !userData;
  if (loading || isWaitingForUserData) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="text-blue-400 text-4xl">
          <FiRefreshCw />
        </motion.div>
        <span className="text-white">{t("loading")}</span>
      </div>
    </div>
  );

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  /* Admin-only route check */
  if (requireAdmin && !canAccessAdmin()) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="bg-gray-800/50 border border-yellow-500/50 rounded-xl p-8 max-w-md text-center">
        <FiAlertCircle className="text-yellow-400 text-5xl mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">{t("permission_denied")}</h1>
        <p className="text-gray-400 mb-6">{t("viewer_no_access")}</p>
        <button onClick={() => window.location.href = "/company"}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-xl transition-colors">
          {t("back_to_dashboard")}
        </button>
      </div>
    </div>
  );

  /* Role-level check */
  if (requireRole && !hasRole(requireRole)) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="bg-gray-800/50 border border-yellow-500/50 rounded-xl p-8 max-w-md text-center">
        <FiAlertCircle className="text-yellow-400 text-5xl mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">{t("permission_denied")}</h1>
        <p className="text-gray-400 mb-6">{t("viewer_no_access")}</p>
        <button onClick={() => window.location.href = "/company"}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-xl transition-colors">
          {t("back_to_dashboard")}
        </button>
      </div>
    </div>
  );

  /* Tenant suspended */
  if (!requireAdmin && tenant?.status === "suspended") return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="bg-gray-800/50 border border-red-500/50 rounded-xl p-8 max-w-md text-center">
        <div className="text-red-400 text-5xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold text-white mb-2">{t("account_suspended")}</h1>
        <p className="text-gray-400 mb-4">{t("account_suspended_desc")}</p>
        {tenant?.name && (
          <div className="bg-gray-700/50 rounded-xl p-4 mb-4">
            <p className="text-gray-400 text-sm mb-1">{t("col_company")}:</p>
            <p className="text-white font-medium">{tenant.name}</p>
          </div>
        )}
        <p className="text-gray-500 text-sm">{t("contact_support")}</p>
      </div>
    </div>
  );

  return children;
};

export default ProtectedRoute;
